import * as proto from 'src/proto/compiled-protos';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule } from '@ngx-translate/core';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { BackendService } from '../../services/backend.service';
import { Formatter } from '../../util/formatter';
import { CompactTextColumn, DataTableColumn, StringColumn } from 'src/app/general/components/data-table/data-table-column';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { SessionService } from '../../services/session.service';
import { DataTableComponent } from 'src/app/general/components/data-table/data-table.component';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { IndeterminatePaginatorComponent } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator.component';
import { IndeterminatePaginatorData, IndeterminatePaginatorModel } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator-model';
import { EventToolbarModel } from '../../model/event-toolbar-model';
import { map, Observable, of } from 'rxjs';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// <app-location-events locationId="{{location.id}}"></app-location-events>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-location-events',
  standalone: true,
  imports: [
    CommonModule,
    LoadableContentComponent,
    TranslateModule,
    MatCardModule,
    DataTableComponent,
    IndeterminatePaginatorComponent
  ],
  templateUrl: './location-events.component.html',
  styleUrls: ['./location-events.component.css']
})
export class LocationEventsComponent implements IndeterminatePaginatorModel<proto.waiternow.common.IEventProto>, OnInit, OnChanges {
  @Input() locationId!: string;

  eventsLoadingController: LoadingController;

  events: Array<proto.waiternow.common.IEventProto>;
  columns: Array<DataTableColumn<proto.waiternow.common.IEventProto>>;

  initialPaginatorData: IndeterminatePaginatorData<proto.waiternow.common.IEventProto>;
  paginatorModel: IndeterminatePaginatorModel<proto.waiternow.common.IEventProto>;

  eventToolbarModel: EventToolbarModel;

  constructor(
      private backendService: BackendService,
      private sessionService: SessionService,
      private changeDetectorRef: ChangeDetectorRef) {
    this.eventsLoadingController = new LoadingController();

    this.events = new Array();
    this.columns = [
      new CompactTextColumn(
        /* name= */ 'id',
        /* translateId= */ 'id',
        /* valueExtractor= */  event => event.id,
        /* compactTextLength= */ 6),
      new CompactTextColumn(
        /* name= */ 'device_id',
        /* translateId= */ 'device_id',
        /* valueExtractor= */  event => event.source?.deviceId,
        /* compactTextLength= */ 6),
      new StringColumn(
        /* name= */ 'name',
        /* translateId= */ 'name',
        /* valueExtractor= */  event => event.name),
      new StringColumn(
        /* name= */ 'summary',
        /* translateId= */ 'summary',
        /* valueExtractor= */  event => event.summary,
        /* hideInCompactMode= */ true),
      new StringColumn(
        /* name= */ 'clientType',
        /* translateId= */ 'client_type',
        /* valueExtractor= */  event => Formatter.formatClientType(event.redundantData?.clientType),
        /* hideInCompactMode= */ true),
      new StringColumn(
        /* name= */ 'deviceType',
        /* translateId= */ 'device_type',
        /* valueExtractor= */  event => Formatter.formatDeviceType(event.redundantData?.deviceType),
        /* hideInCompactMode= */ true),
      new StringColumn(
        /* name= */ 'creationTime',
        /* translateId= */ 'creation_time',
        /* valueExtractor= */  event => Formatter.formatTimestampProto(event.creationTime),
        /* hideInCompactMode= */ true)
      ];

      this.initialPaginatorData = { data: new Array(), continuationToken: ''};
      this.paginatorModel = this;

      this.eventToolbarModel = new EventToolbarModel(this.sessionService);
  }

  ngOnInit(): void {
    this.loadInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('locationId', changes)) {
      this.ngOnInit();
    }
  }

  updatePage(page: proto.waiternow.common.IEventProto[]): void {
    this.events = page;
    // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
    this.changeDetectorRef.detectChanges();
  }

  fetchData(continuationToken: string | null | undefined): Observable<IndeterminatePaginatorData<proto.waiternow.common.IEventProto>> {
    if (!this.locationId) {
      return of({data: [], continuationToken: ''});
    }

    return this.backendService.findLocationEventsReturnObservable(this.locationId, continuationToken)
      .pipe(
        map(locationEvents  => {
          const paginatorData: IndeterminatePaginatorData<proto.waiternow.common.IEventProto> = {data: [], continuationToken: continuationToken};
          if (locationEvents) {
            paginatorData.data = locationEvents.events;
            paginatorData.continuationToken = locationEvents.continuationToken;
          }
          return paginatorData;
        })
      );
  }

  public loadInitialData(): void {
    this.eventsLoadingController.onLoadingStarted(LoadingMessage.withMessageTranslateId('loading_events'));
    this.fetchData(/* continuationToken= */ null).subscribe(
      {
        next: paginatorData => {
          this.initialPaginatorData = paginatorData;
          this.eventsLoadingController.onSuccess();
          // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          this.eventsLoadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_loading_events'));
        }
      }
    );
  }
}
